// Cache condivisa delle miniature allegati.
// Evita di ri-scaricare le thumb dal backend quando si riapre una griglia:
// - in-memory (Map) per la sessione corrente -> apertura istantanea;
// - persistenza leggera su localStorage con TTL e limite di dimensione (sopravvive ai reload).
// Chiave: id allegato (ObjectId, globalmente unico).
import api from "./api";

const mem = new Map(); // aid -> { url, ts }
const inflight = new Map(); // aid -> Promise
const LS_KEY = "pgp_thumb_cache_v2";
const TTL_MS = 45 * 60 * 1000; // 45 min: inferiore alla scadenza degli URL firmati
const MAX_ENTRIES = 400;

let persisted = {};
try { persisted = JSON.parse(localStorage.getItem(LS_KEY) || "{}"); } catch { persisted = {}; }

function fresh(entry) { return !!entry && (Date.now() - entry.ts) < TTL_MS; }

function saveLS() {
  let entries = Object.entries(persisted)
    .filter(([, e]) => fresh(e))
    .sort((a, b) => b[1].ts - a[1].ts)
    .slice(0, MAX_ENTRIES);
  // Riprova dimezzando se si supera la quota di localStorage.
  while (entries.length) {
    try {
      localStorage.setItem(LS_KEY, JSON.stringify(Object.fromEntries(entries)));
      persisted = Object.fromEntries(entries);
      return;
    } catch {
      entries = entries.slice(0, Math.floor(entries.length / 2));
    }
  }
  try { localStorage.removeItem(LS_KEY); } catch { /* */ }
  persisted = {};
}

// Ritorna l'url della miniatura se presente in cache (memoria o localStorage) e ancora valido.
export function getCachedThumb(aid) {
  const m = mem.get(aid);
  if (fresh(m)) return m.url;
  const p = persisted[aid];
  if (fresh(p)) { mem.set(aid, p); return p.url; }
  return null;
}

// Ritorna l'URL firmato dell'ORIGINALE (direct-to-storage) per l'anteprima PDF quando la
// miniatura non è ancora stata generata. Cache breve in memoria (gli URL firmati scadono).
const pdfMem = new Map();
export async function fetchPdfPreview(pid, aid) {
  const m = pdfMem.get(aid);
  if (fresh(m)) return m.url;
  try {
    const { data } = await api.get(`/studio/pratiche/${pid}/allegati-files/${aid}/content-url`);
    const url = data?.url || null;
    if (url) pdfMem.set(aid, { url, ts: Date.now() });
    return url;
  } catch { return null; }
}

// Ritorna l'url della miniatura, usando la cache; altrimenti la scarica dal backend una sola volta.
// Fallback: se la miniatura pre-generata non esiste ancora (backfill non completato) e il file è
// un'immagine, usa l'URL firmato dell'originale (/content-url, direct-to-storage, senza passare i
// byte dal backend) così l'anteprima è comunque visibile. Il PDF senza thumb mostra l'icona.
export async function fetchThumb(pid, aid, contentType = "") {
  const cached = getCachedThumb(aid);
  if (cached) return cached;
  if (inflight.has(aid)) return inflight.get(aid);
  const isImg = String(contentType).toLowerCase().startsWith("image/");
  const promise = (async () => {
    try {
      const { data } = await api.get(`/studio/pratiche/${pid}/allegati-files/${aid}/thumb`);
      let url = data?.url || null;
      if (!url && isImg) {
        try {
          const r = await api.get(`/studio/pratiche/${pid}/allegati-files/${aid}/content-url`);
          url = r.data?.url || null;
        } catch { /* nessun presigned disponibile (es. GridFS) */ }
      }
      if (url) {
        const entry = { url, ts: Date.now() };
        mem.set(aid, entry);
        persisted[aid] = entry;
        saveLS();
      }
      return url;
    } finally {
      inflight.delete(aid);
    }
  })();
  inflight.set(aid, promise);
  return promise;
}
